import clsx from 'clsx';
import { AlertCircle, AlertTriangle, Info } from 'lucide-react';
import type { SEOIssue } from '@/shared/types';

interface IssuesListProps {
  issues: SEOIssue[];
  maxItems?: number;
  compact?: boolean;
}

export function IssuesList({ issues, maxItems, compact = false }: IssuesListProps) {
  const displayIssues = maxItems ? issues.slice(0, maxItems) : issues;
  const remaining = maxItems && issues.length > maxItems ? issues.length - maxItems : 0;

  if (issues.length === 0) {
    return (
      <div className="text-center py-4 text-surface-500">
        <Info className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No issues found</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {displayIssues.map((issue, index) => (
        <IssueItem key={`${issue.type}-${index}`} issue={issue} compact={compact} />
      ))}
      {remaining > 0 && (
        <p className="text-sm text-surface-500 text-center py-2">
          +{remaining} more issues
        </p>
      )}
    </div>
  );
}

function IssueItem({ issue, compact }: { issue: SEOIssue; compact?: boolean }) {
  const severityConfig = {
    critical: {
      icon: AlertCircle,
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      textColor: 'text-red-700',
      iconColor: 'text-red-500',
    },
    warning: {
      icon: AlertTriangle,
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      textColor: 'text-yellow-700',
      iconColor: 'text-yellow-500',
    },
    info: {
      icon: Info,
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-700',
      iconColor: 'text-blue-500',
    },
  };

  const config = severityConfig[issue.severity];
  const Icon = config.icon;

  return (
    <div
      className={clsx(
        'rounded-lg border',
        config.bgColor,
        config.borderColor,
        compact ? 'p-2' : 'p-3'
      )}
    >
      <div className="flex items-start gap-2">
        <Icon className={clsx('flex-shrink-0 mt-0.5', config.iconColor, compact ? 'w-4 h-4' : 'w-5 h-5')} />
        <div className="flex-1 min-w-0">
          <p className={clsx('font-medium', config.textColor, compact ? 'text-xs' : 'text-sm')}>
            {issue.message}
          </p>
          {!compact && issue.recommendation && (
            <p className="text-xs text-surface-600 mt-1">
              {issue.recommendation}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export function IssuesSummary({ issues, warnings }: { issues: SEOIssue[]; warnings: SEOIssue[] }) {
  const criticalCount = issues.length;
  const warningCount = warnings.filter(w => w.severity === 'warning').length;
  const infoCount = warnings.filter(w => w.severity === 'info').length;

  return (
    <div className="flex items-center gap-4">
      {criticalCount > 0 && (
        <div className="flex items-center gap-1.5">
          <AlertCircle className="w-4 h-4 text-red-500" />
          <span className="text-sm font-medium text-red-700">{criticalCount} Critical</span>
        </div>
      )}
      {warningCount > 0 && (
        <div className="flex items-center gap-1.5">
          <AlertTriangle className="w-4 h-4 text-yellow-500" />
          <span className="text-sm font-medium text-yellow-700">{warningCount} Warnings</span>
        </div>
      )}
      {infoCount > 0 && (
        <div className="flex items-center gap-1.5">
          <Info className="w-4 h-4 text-blue-500" />
          <span className="text-sm font-medium text-blue-700">{infoCount} Info</span>
        </div>
      )}
      {criticalCount === 0 && warningCount === 0 && infoCount === 0 && (
        <span className="text-sm text-green-600 font-medium">No issues</span>
      )}
    </div>
  );
}
